import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, AlertCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BRAND_ASSETS_AUTHORIZED, BUSINESS_EMAIL, BUSINESS_PHONE_DISPLAY, LOGO_AVANTI_COLOR } from '../data/contact';
import { submitInquiry } from '../utils/formSubmission';
import { trackEvent } from '../utils/analytics';
import { Reveal } from './Reveal';
type Status = 'idle' | 'submitting' | 'success' | 'error';
interface FieldErrors {
  fullName?: string;
  phone?: string;
  email?: string;
  consent?: string;
}
const CONTACT_METHODS = [{
  value: 'phone',
  en: 'Phone',
  es: 'Teléfono'
}, {
  value: 'text',
  en: 'Text message',
  es: 'Mensaje de texto'
}, {
  value: 'whatsapp',
  en: 'WhatsApp',
  es: 'WhatsApp'
}, {
  value: 'email',
  en: 'Email',
  es: 'Correo electrónico'
}];
const TIMELINES = [{
  value: 'immediately',
  en: 'Immediately',
  es: 'Inmediatamente'
}, {
  value: '30-days',
  en: 'Within 30 days',
  es: 'En 30 días'
}, {
  value: '3-months',
  en: 'Within 3 months',
  es: 'En 3 meses'
}, {
  value: '6-months',
  en: 'Within 6 months',
  es: 'En 6 meses'
}, {
  value: 'exploring',
  en: 'Just exploring',
  es: 'Solo explorando'
}];
const inputClasses = 'mt-2 w-full border border-midnight/20 bg-white px-4 py-3 text-[15px] text-ink transition-colors duration-150 ease-out placeholder:text-ink/40 focus:border-gold focus:outline-none';
export function ContactSection() {
  const {
    lang,
    t
  } = useLanguage();
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<FieldErrors>({});
  const mountedAt = useRef(Date.now());
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    preferredContact: 'whatsapp',
    timeline: 'exploring',
    tourDate: '',
    message: '',
    consent: false,
    // Honeypot — must stay empty. Bots that fill it are rejected silently.
    website: ''
  });
  const update = <K extends keyof typeof form,>(key: K, value: (typeof form)[K]) => {
    setForm(current => ({
      ...current,
      [key]: value
    }));
  };
  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (form.fullName.trim().length < 2) {
      next.fullName = t('Please enter your full name.', 'Ingresa tu nombre completo.');
    }
    if (!/^[0-9+()\-.\s]{7,}$/.test(form.phone.trim())) {
      next.phone = t('Please enter a valid phone number.', 'Ingresa un número de teléfono válido.');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      next.email = t('Please enter a valid email address.', 'Ingresa un correo electrónico válido.');
    }
    if (!form.consent) {
      next.consent = t('Consent is required to continue.', 'Es necesario aceptar para continuar.');
    }
    return next;
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStatus('idle');
      return;
    }
    // Bot protection: honeypot + minimum time-on-form.
    if (form.website !== '' || Date.now() - mountedAt.current < 2500) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    const result = await submitInquiry({
      fullName: form.fullName,
      phone: form.phone,
      email: form.email,
      preferredContact: form.preferredContact,
      timeline: form.timeline,
      tourDate: form.tourDate,
      message: form.message,
      consent: form.consent,
      language: lang
    });
    if (result.ok) {
      // Event carries no lead contents.
      trackEvent('lead_form_submit', {
        location: 'contact_form'
      });
      setStatus('success');
      setForm({
        fullName: '',
        phone: '',
        email: '',
        preferredContact: 'whatsapp',
        timeline: 'exploring',
        tourDate: '',
        message: '',
        consent: false,
        website: ''
      });
    } else {
      setStatus('error');
    }
  };
  return <section id="contact" className="w-full bg-ivory py-20 lg:py-28" data-id="element-104" data-mp="3ac6njhuzpsqp">
      <div className="mx-auto max-w-editorial px-5 lg:px-8" data-id="element-105" data-mp="3f4pye72am9rt">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-106" data-mp="3f6ards8ylknk">
          <Reveal className="lg:col-span-5" data-id="element-107" data-mp="31eml45le5qei">
            <h2 className="font-display text-[36px] font-semibold uppercase leading-[1.02] text-midnight sm:text-[46px] lg:text-[56px]" data-id="element-108" data-mp="35edi4r1ih2rh">
              {t('Make It Yours', 'Hazla Tuya')}
            </h2>
            <div className="gold-rule mt-6 w-24" data-id="element-109" data-mp="1xo7otyt58wj" />
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-ink/80 lg:text-base" data-id="element-110" data-mp="1xej9atev9sy">
              {t('Request additional information or schedule a private tour of this furnished Davenport townhome.', 'Solicita información adicional o programa una visita privada a esta townhome amoblada en Davenport.')}
            </p>
            <div className="mt-8 space-y-1 text-[14px] text-ink/75" data-id="element-111" data-mp="2doijg3rrk0q5">
              <p data-id="element-112" data-mp="2566t72g6hldd">{BUSINESS_PHONE_DISPLAY}</p>
              <p data-id="element-113" data-mp="254vngx36a3jm">{BUSINESS_EMAIL}</p>
              <p className="text-[12px] text-ink/60" data-id="element-114" data-mp="214va7frt7ye5">
                Veronica Gomez Santana •{' '}
                {t('Affiliated with Avanti Way Realty LLC', 'Afiliada a Avanti Way Realty LLC')}
              </p>
            </div>
            {BRAND_ASSETS_AUTHORIZED && <img src={LOGO_AVANTI_COLOR} alt="Avanti Way Realty LLC." className="mt-6 h-auto w-[150px]" width={150} height={30} loading="lazy" decoding="async" data-id="element-115" data-mp="2a3gnowifcn82" />}
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.06} data-id="element-116" data-mp="1rf1xgbfxld8b">
            {status === 'success' ? <div role="status" className="flex h-full flex-col items-start justify-center border border-gold/50 bg-white p-8 lg:p-12" data-id="element-117" data-mp="13phe3rfv8dyi">
                <CheckCircle2Icon className="h-9 w-9 text-gold" aria-hidden="true" data-id="element-118" data-mp="yoa2tieikwn3" />
                <p className="mt-5 max-w-md text-[16px] leading-relaxed text-ink/85" data-id="element-119" data-mp="2ysdri2m4nv1o">
                  {t('Thank you. Veronica will contact you shortly with additional information about the property.', 'Gracias. Veronica se comunicará contigo próximamente con información adicional sobre la propiedad.')}
                </p>
                <button type="button" onClick={() => setStatus('idle')} className="label-caps mt-8 text-midnight underline decoration-gold/60 underline-offset-4" data-id="element-120" data-mp="2fvcj07td7csz">
                  {t('Send another request', 'Enviar otra solicitud')}
                </button>
              </div> : <form noValidate onSubmit={handleSubmit} className="bg-white p-6 lg:p-10" data-id="element-121" data-mp="450gqwc30vp8">
                {status === 'error' && <div role="alert" className="mb-6 flex items-start gap-3 border border-red-300 bg-red-50 p-4 text-[13px] text-red-800" data-id="element-122" data-mp="91qx7krsttb6">
                    <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-123" data-mp="e8p9z4dxzpew" />
                    <span data-id="element-124" data-mp="9g4hq7aopkdh">
                      {t('We could not send your request. Please try again, or contact Veronica directly by phone or WhatsApp.', 'No pudimos enviar tu solicitud. Inténtalo de nuevo o comunícate directamente con Veronica por teléfono o WhatsApp.')}
                    </span>
                  </div>}

                <div className="grid gap-5 sm:grid-cols-2" data-id="element-125" data-mp="3mo903ujbgrpq">
                  <div className="sm:col-span-2" data-id="element-126" data-mp="3in5ib2hpkllv">
                    <label htmlFor="fullName" className="label-caps text-ink/60" data-id="element-127" data-mp="3ioqgd238emoh">
                      {t('Full name', 'Nombre completo')}
                    </label>
                    <input id="fullName" name="fullName" type="text" autoComplete="name" required value={form.fullName} onChange={event => update('fullName', event.target.value)} aria-invalid={Boolean(errors.fullName)} aria-describedby={errors.fullName ? 'fullName-error' : undefined} className={inputClasses} data-id="element-128" data-mp="1gwbxo0sv5e70" />
                    {errors.fullName && <p id="fullName-error" className="mt-2 text-[12px] text-red-700" data-id="element-129" data-mp="1lfnwnakq4y10">
                        {errors.fullName}
                      </p>}
                  </div>

                  <div data-id="element-130" data-mp="1cepznc8pdqrx">
                    <label htmlFor="phone" className="label-caps text-ink/60" data-id="element-131" data-mp="1cgakga57tvcl">
                      {t('Telephone number', 'Número de teléfono')}
                    </label>
                    <input id="phone" name="phone" type="tel" autoComplete="tel" required value={form.phone} onChange={event => update('phone', event.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} className={inputClasses} data-id="element-132" data-mp="36rp9spubdsrt" />
                    {errors.phone && <p id="phone-error" className="mt-2 text-[12px] text-red-700" data-id="element-133" data-mp="3uoc1wu7tswps">
                        {errors.phone}
                      </p>}
                  </div>

                  <div data-id="element-134" data-mp="8g2rc42kmxjy">
                    <label htmlFor="email" className="label-caps text-ink/60" data-id="element-135" data-mp="8hnlhskz4eha">
                      {t('Email address', 'Correo electrónico')}
                    </label>
                    <input id="email" name="email" type="email" autoComplete="email" required value={form.email} onChange={event => update('email', event.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} className={inputClasses} data-id="element-136" data-mp="2a8h6wwhn7px7" />
                    {errors.email && <p id="email-error" className="mt-2 text-[12px] text-red-700" data-id="element-137" data-mp="1d28sfn30pltv">
                        {errors.email}
                      </p>}
                  </div>

                  <div data-id="element-138" data-mp="13cfkcw233oud">
                    <label htmlFor="preferredContact" className="label-caps text-ink/60" data-id="element-139" data-mp="13cxlo73lst95">
                      {t('Preferred contact method', 'Método de contacto preferido')}
                    </label>
                    <select id="preferredContact" name="preferredContact" value={form.preferredContact} onChange={event => update('preferredContact', event.target.value)} className={inputClasses} data-id="element-140" data-mp="1w0tgt7diyd3t">
                      {CONTACT_METHODS.map(method => <option key={method.value} value={method.value} data-id="element-141" data-mp="2ia1od3dl5n8c">
                          {lang === 'en' ? method.en : method.es}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-142" data-mp="2r8dobw0cdxdg">
                    <label htmlFor="timeline" className="label-caps text-ink/60" data-id="element-143" data-mp="2w0wzynfgyifm">
                      {t('Buying timeline', 'Tiempo estimado para comprar')}
                    </label>
                    <select id="timeline" name="timeline" value={form.timeline} onChange={event => update('timeline', event.target.value)} className={inputClasses} data-id="element-144" data-mp="3122xcyk2qtvh">
                      {TIMELINES.map(option => <option key={option.value} value={option.value} data-id="element-145" data-mp="15xwg1hka3adr">
                          {lang === 'en' ? option.en : option.es}
                        </option>)}
                    </select>
                  </div>

                  <div className="sm:col-span-2" data-id="element-146" data-mp="1evow4nxtqm27">
                    <label htmlFor="tourDate" className="label-caps text-ink/60" data-id="element-147" data-mp="1tlcyztlnlss1">
                      {t('Preferred tour date', 'Fecha preferida para la visita')}
                    </label>
                    <input id="tourDate" name="tourDate" type="date" value={form.tourDate} onChange={event => update('tourDate', event.target.value)} className={inputClasses} data-id="element-148" data-mp="22ndv2n55i8s4" />
                  </div>

                  <div className="sm:col-span-2" data-id="element-149" data-mp="49ubb6hs5ap7">
                    <label htmlFor="message" className="label-caps text-ink/60" data-id="element-150" data-mp="3qa038b4b6oqq">
                      {t('Message', 'Mensaje')}
                    </label>
                    <textarea id="message" name="message" rows={4} value={form.message} onChange={event => update('message', event.target.value)} className={`${inputClasses} resize-y`} data-id="element-151" data-mp="3gj5ff2bezh36" />
                  </div>
                </div>

                {/* Honeypot field — hidden from users and assistive technology. */}
                <div className="hidden" aria-hidden="true" data-id="element-152" data-mp="2xm4655atuutf">
                  <label htmlFor="website" data-id="element-153" data-mp="32ikxe8dbppaq">Website</label>
                  <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={event => update('website', event.target.value)} data-id="element-154" data-mp="32l7d4vadbb41" />
                </div>

                <div className="mt-7 flex items-start gap-3" data-id="element-155" data-mp="2rxcmtdjlg59q">
                  <input id="consent" name="consent" type="checkbox" checked={form.consent} onChange={event => update('consent', event.target.checked)} aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'consent-error' : undefined} className="mt-1 h-4 w-4 shrink-0 accent-[#C89B43]" data-id="element-156" data-mp="2mwf6z7m23nbe" />
                  <div data-id="element-157" data-mp="1z7nlez2z237l">
                    <label htmlFor="consent" className="block text-[13px] leading-relaxed text-ink/80" data-id="element-158" data-mp="1z85ghsdwebdx">
                      {t('I agree to be contacted regarding this property. Submitting this form does not create a brokerage agreement.', 'Acepto ser contactado en relación con esta propiedad. Enviar este formulario no crea un acuerdo de representación inmobiliaria.')}{' '}
                      <Link to="/privacy-policy" className="underline decoration-gold/60 underline-offset-2 hover:text-midnight" data-id="element-159" data-mp="apuf0426npzw">
                        {t('Privacy Policy', 'Política de Privacidad')}
                      </Link>
                    </label>
                    {errors.consent && <p id="consent-error" className="mt-2 text-[12px] text-red-700" data-id="element-160" data-mp="3n5obw936bmqn">
                        {errors.consent}
                      </p>}
                  </div>
                </div>

                <button type="submit" disabled={status === 'submitting'} className="mt-8 w-full bg-gold px-6 py-4 font-display text-[13px] font-medium uppercase tracking-[0.16em] text-midnight transition-colors duration-150 ease-out hover:bg-gold-soft disabled:opacity-70" data-id="element-161" data-mp="rn2pyiu9w48">
                  {status === 'submitting' ? t('Sending…', 'Enviando…') : t('Request Property Information', 'Solicitar Información')}
                </button>

                <p className="mt-4 text-[11px] leading-relaxed text-ink/55" data-id="element-162" data-mp="gadggv76xkyz">
                  {t('We collect only the information needed to respond to your inquiry. We never request financial or identification documents.', 'Solo recopilamos la información necesaria para responder a tu consulta. Nunca solicitamos documentos financieros o de identificación.')}
                </p>
              </form>}
          </Reveal>
        </div>
      </div>
    </section>;
}